import { useSimulationStore } from '@/store/useSimulationStore';
import Logo from '/icons/octopus_logo.png';
import { SENTIMENT_CHART_STEPS } from '@/_lib/constants';

const DonorSentimentSection = ({
  donorSentiment
}: {
  donorSentiment: {
    summary: string;
    indicators: { name: string; score: number }[];
  };
}) => {
  const simulationType = useSimulationStore(state => state.simulationType);

  const getChipColorsByScore = (value: number) => {
    const bgColors: Record<number, string> = {
      5: 'bg-green-100 text-brandPrimaryGreen',
      4: 'bg-yellow-100 text-primary',
      3: 'bg-red-100 text-darkOrange'
    };

    const score = value ?? 5;
    return bgColors[score] || 'bg-green-100 text-brandPrimaryGreen';
  };

  const getProgressBarBgColorByScore = (value: number) => {
    const bgColors: Record<number, string> = {
      5: 'bg-brandPrimaryGreen',
      4: 'bg-primary',
      3: 'bg-darkOrange'
    };

    const score = value ?? 5;
    return bgColors[score] || 'bg-brandPrimaryGreen';
  };

  const getProgressBarValue = (score: number) => {
    switch (score) {
      case 5:
        return 100;
      case 4:
        return 50;
      case 3:
        return 10;
      default:
        return 100;
    }
  };

  const getChipLabelByScore = (score: number) => {
    switch (score) {
      case 5:
        return 'High';
      case 4:
        return 'Medium';
      case 3:
        return 'Low';
      default:
        return 'High';
    }
  };

  return (
    <div className="flex flex-col gap-6 p-4 bg-neutral-50 border-1 border-cardBorderColor rounded-xl">
      <div className="flex-none">
        <div className="pb-2 flex items-center justify-between">
          <h3 className="text-xl leading-8 font-semibold">Donor Sentiment</h3>
        </div>
        <p className="text-sm font-normal text-neutral-500">How your conversation resonated with the donor.</p>
      </div>

      {!donorSentiment || donorSentiment?.indicators?.length === 0 ? (
        <div className="flex w-full h-[60%] justify-center items-center">
          <p className="text-brandNeutralGray/50 font-semibold text-2xl">No sentiment data</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-6">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-start-2 col-span-2 grid grid-cols-3 gap-2">
              {SENTIMENT_CHART_STEPS.map((step, index) => {
                return (
                  <div key={index} className="flex items-center justify-center">
                    <p className="text-center text-xs font-semibold">{step}</p>
                  </div>
                );
              })}
            </div>
          </div>
          {donorSentiment?.indicators?.map((indicator, index) => {
            const indicatorFallback = ![-1, 0, 1].includes(indicator.score) ? 0 : indicator.score;
            return (
              <div key={index} className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <p className="text-sm font-semibold text-neutral-700">{indicator?.name}</p>
                </div>
                <div className="col-span-2 flex items-center">
                  <div className="grid grid-cols-3 h-3 w-full">
                    <div className="bg-buttonYellow rounded-l-md flex items-center justify-center relative">
                      {indicatorFallback === -1 && (
                        <img src={Logo} alt="Octopus" width={28} className="object-contain absolute" />
                      )}
                    </div>
                    <div className="bg-neutral-300 flex items-center justify-center">
                      {indicatorFallback === 0 && (
                        <img src={Logo} alt="Octopus" width={28} className="object-contain absolute" />
                      )}
                    </div>
                    <div className="bg-brandPrimaryGreen rounded-r-md flex items-center justify-center">
                      {indicatorFallback === 1 && (
                        <img src={Logo} alt="Octopus" width={28} className="object-contain absolute" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DonorSentimentSection;
